// @ts-strict-ignore
import { gql, useApolloClient } from "@apollo/client";
import { DashboardCard } from "@dashboard/components/Card";
import { Multiselect } from "@dashboard/components/Combobox";
import { useShippingRateExclusions } from "@dashboard/shipping/hooks/useShippingRateExclusions";
import { Box, Option, Text } from "@saleor/macaw-ui-next";
import { useEffect, useState } from "react";
import { defineMessages, useIntl } from "react-intl";

const searchProductTypesWithSlug = gql`
  query SearchProductTypesWithSlug($first: Int!, $query: String!) {
    productTypes(first: $first, filter: { search: $query }) {
      edges {
        node {
          slug
          name
        }
      }
    }
  }
`;

const messages = defineMessages({
  title: { id: "1zOWdW", defaultMessage: "Excluded Product Types" },
  hint: {
    id: "K3it/I",
    defaultMessage:
      "Products of these types will not be offered this rate. Resolved live at checkout, so products added to a type later are excluded automatically.",
  },
  label: { id: "+LUv1R", defaultMessage: "Product types" },
  save: { id: "jvo0vs", defaultMessage: "Save" },
});

export interface ShippingRateExclusionsProps {
  // Edit mode — self-fetching and self-saving
  zoneName?: string;
  rateName?: string;
  // Controlled/buffered mode — used on the create page before the rate exists
  value?: Option[];
  onChange?: (value: Option[]) => void;
}

export const ShippingRateExclusions = ({
  zoneName,
  rateName,
  value: controlledValue,
  onChange,
}: ShippingRateExclusionsProps) => {
  const intl = useIntl();
  const client = useApolloClient();
  const buffered = !!onChange;

  // Edit-mode hook (skipped in buffered mode since rateName is empty there)
  const { productTypes, saving, save } = useShippingRateExclusions(
    buffered ? "" : (zoneName ?? ""),
    buffered ? "" : (rateName ?? ""),
  );

  const [internalValue, setInternalValue] = useState<Option[]>([]);
  const [options, setOptions] = useState<Option[]>([]);
  const [initialised, setInitialised] = useState(false);

  const value = buffered ? (controlledValue ?? []) : internalValue;

  useEffect(() => {
    if (buffered || initialised || productTypes.length === 0) return;

    setInternalValue(productTypes.map(pt => ({ value: pt.slug, label: pt.name })));
    setInitialised(true);
  }, [buffered, productTypes, initialised]);

  const fetchOptions = (query: string) => {
    client
      .query({
        query: searchProductTypesWithSlug,
        variables: { first: 10, query },
      })
      .then(({ data }) => {
        const edges = data?.productTypes?.edges ?? [];

        setOptions(
          edges.map((e: { node: { slug: string; name: string } }) => ({
            value: e.node.slug,
            label: e.node.name,
          })),
        );
      });
  };

  const handleChange = (next: Option[]) => {
    if (buffered) {
      onChange(next);
    } else {
      setInternalValue(next);
    }
  };

  const handleSave = () => {
    save({ product_type_slugs: value.map(v => v.value) });
  };

  return (
    <DashboardCard>
      <DashboardCard.Header>
        <DashboardCard.Title>{intl.formatMessage(messages.title)}</DashboardCard.Title>
      </DashboardCard.Header>
      <DashboardCard.Content>
        <Text size={2} color="default2" display="block" marginBottom={3}>
          {intl.formatMessage(messages.hint)}
        </Text>
        <Multiselect
          name="excludedProductTypes"
          label={intl.formatMessage(messages.label)}
          options={options}
          value={value}
          fetchOptions={fetchOptions}
          onChange={e => handleChange(e.target.value as unknown as Option[])}
          alwaysFetchOnFocus
        />
        {!buffered && (
          <Box display="flex" justifyContent="flex-end" marginTop={4}>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: "6px 16px",
                fontSize: 13,
                fontWeight: 600,
                borderRadius: 6,
                border: "none",
                backgroundColor: saving ? "#94a3b8" : "#1d4ed8",
                color: "#fff",
                cursor: saving ? "not-allowed" : "pointer",
              }}
            >
              {saving ? "Saving…" : intl.formatMessage(messages.save)}
            </button>
          </Box>
        )}
      </DashboardCard.Content>
    </DashboardCard>
  );
};

export default ShippingRateExclusions;
